import { Car, SlidersHorizontal } from "lucide-react";
import React, { useEffect, useState } from "react";
import PolicyCard from "../../components/policies/PolicyCard";
import PolicyActionsDropdown from "../../components/policies/PolicyActionsDropdown";
import ConfirmCancelModal from "../../components/policies/ConfirmCancelModal";
import CancelSuccessModal from "../../components/policies/CancelSuccessModal";
import PolicyViewModal from "../../components/policies/PolicyViewModal";
import PolicyFilterDrawer from "../../components/policies/PolicyFilterDrawer";
import { useNavigate } from "react-router-dom";
import policyService from "../../api/PolicyService";
import { toast } from "react-toastify";
import Loader from "../../components/misc/Loader";
import EmptyState, { EmptyStatePresets } from "../../components/misc/EmptyState";
import PolicyholderCheckModal from "../../components/claims/PolicyholderCheckModal";
import { buildClaimFormPolicy } from "../../utils/claimHelpers";

const Policy = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastCancelled, setLastCancelled] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [claimTarget, setClaimTarget] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: { active: true, inactive: false },
    policyType: "all",
    routeType: "all",
    duration: "all"
  });
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (policies.length > 0) {
      setFilteredPolicies(policies);
    }
  }, [policies]);

  useEffect(() => {
    const fetchPurchasedPolicies = async () => {
      setIsLoading(true);
      try {
        const response = await policyService.getPurchasedPolicies();
        
        if (response.status && response.data?.status_code === 200) {
          const purchasedPolicies = response.data.data || [];
          
          // Fetch full policy details for each purchased policy
          const enrichedPolicies = await Promise.all(
            purchasedPolicies.map(async (purchasedPolicy) => {
              try {
                const policyDetails = await policyService.getPolicy(
                  purchasedPolicy.insurance_policy_slug
                );
                
                if (policyDetails.status && policyDetails.data?.data) {
                  return {
                    ...purchasedPolicy,
                    insurance_policy: policyDetails.data.data,
                  };
                }
                return purchasedPolicy;
              } catch (error) {
                console.error(
                  `Failed to fetch policy details for ${purchasedPolicy.insurance_policy_slug}:`,
                  error
                );
                return purchasedPolicy;
              }
            })
          );
          
          setPolicies(enrichedPolicies);
        } else {
          toast.error(
            response.message ||
              "An error occurred while fetching purchased policies"
          );
        }
      } catch (error) {
        toast.error(
          error.response?.data?.reason ||
            "An error occurred while fetching purchased policies"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchPurchasedPolicies();
  }, []);

  useEffect(() => {
    const applyFilters = () => {
      let filtered = [...policies];

      // Filter by policy type (check insurance_policy for short_description and policy_type)
      if (filters.policyType !== "all") {
        filtered = filtered.filter(policy => {
          const purchasedData = policy.purchased_policy_data;
          const insurancePolicy = policy.insurance_policy;
          const insuranceType = purchasedData?.insurance_type || insurancePolicy?.short_description;
          const policyType = insurancePolicy?.policy_type;
          
          // Map filter values to actual types
          if (filters.policyType === "tripsecure_insurance") {
            return insuranceType === "tripsecure_insurance";
          } else if (filters.policyType === "travel_insurance") {
            return insuranceType === "travel_insurance";
          } else if (filters.policyType === "life_insurance") {
            return insuranceType === "life_insurance";
          } else if (filters.policyType === "general_insurance") {
            return insuranceType === "general_insurance";
          }
          // For specific policy types like intercity_travel, okada_rider
          return policyType === filters.policyType;
        });
      }

      // Filter by route type (for TripSecure and travel insurance policies)
      if (filters.routeType !== "all") {
        filtered = filtered.filter(policy => {
          const purchasedData = policy.purchased_policy_data;
          const insurancePolicy = policy.insurance_policy;
          const insuranceType = purchasedData?.insurance_type || insurancePolicy?.short_description;
          const policyType = insurancePolicy?.policy_type;
          const coverageDuration = purchasedData?.coverageDuration;
          
          // Only apply route type filter to TripSecure and Travel insurance
          // Note: okada_rider doesn't have route_type, so skip it
          if (insuranceType === "tripsecure_insurance" || insuranceType === "travel_insurance") {
            // Skip route type filter for okada_rider and daily_commuter as they don't have route_type
            if (policyType === "okada_rider" || policyType === "daily_commuter") {
              return true;
            }
            return coverageDuration?.route_type === filters.routeType;
          }
          return true;
        });
      }

      // Filter by duration
      if (filters.duration !== "all") {
        filtered = filtered.filter(policy => {
          const purchasedData = policy.purchased_policy_data;
          const insurancePolicy = policy.insurance_policy;
          const coverageDuration = purchasedData?.coverageDuration;
          const duration = purchasedData?.duration; // For Life/General Insurance or travel_insurance with string duration
          
          // Helper function to format duration
          const formatDuration = (durationObj) => {
            if (typeof durationObj === 'string') {
              return durationObj; // Already formatted string like "3 hours"
            }
            if (durationObj.display_unit && durationObj.display_value) {
              const unit = durationObj.display_unit;
              const value = durationObj.display_value;
              return `${value} ${value === 1 ? unit.slice(0, -1) : unit}`;
            }
            if (durationObj.hours) {
              const hours = durationObj.hours;
              return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
            }
            return durationObj.toString();
          };
          
          if (coverageDuration) {
            // Travel Insurance / TripSecure format (intercity_travel)
            const policyDuration = formatDuration(coverageDuration);
            return policyDuration === filters.duration;
          } else if (duration) {
            // Life/General Insurance format or travel_insurance with string duration
            if (typeof duration === 'string') {
              return duration === filters.duration;
            }
            return duration === filters.duration;
          } else if (insurancePolicy?.coverage_durations?.length > 0) {
            // For okada_rider or travel_insurance, check coverage_durations in insurance_policy
            const matchingDuration = insurancePolicy.coverage_durations.find(cd => {
              // Handle both object and string formats
              if (typeof cd === 'string') {
                return cd === filters.duration;
              }
              if (cd.duration && typeof cd.duration === 'string') {
                return cd.duration === filters.duration;
              }
              const formatted = formatDuration(cd);
              return formatted === filters.duration;
            });
            return !!matchingDuration;
          }
          return false;
        });
      }

      setFilteredPolicies(filtered);
    };

    applyFilters();
  }, [policies, filters]);

  const handleCancel = (policy) => setCancelTarget(policy);
  const handleConfirmCancel = async () => {
    if (!cancelTarget || isCancelling) return;

    try {
      setIsCancelling(true);
      const slug = cancelTarget.insurance_policy_slug;
      const response = await policyService.requestPolicyCancellation(slug);

      if (!response.status) {
        toast.error(
          response.message || "Failed to request policy cancellation"
        );
        return;
      }

      setLastCancelled(cancelTarget);

      // Mark policy as cancel pending locally; backend will drive final status
      setPolicies((list) =>
        list.map((p) =>
          p.insurance_policy_slug === slug
            ? { ...p, policy_status: "cancel_pending" }
            : p
        )
      );

      setShowSuccess(true);
      setCancelTarget(null);
    } catch (error) {
      console.error("Cancellation request error:", error);
      toast.error(
        error.response?.data?.reason ||
          "An unexpected error occurred while requesting cancellation"
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const handleClaim = (policy) => setClaimTarget(policy);

  const handlePolicyholderChoice = (isPolicyholder) => {
    if (!claimTarget) return;
    const mappedPolicy = buildClaimFormPolicy(claimTarget);
    setClaimTarget(null);
    if (isPolicyholder) {
      navigate("/claim/new/form", {
        state: { policy: mappedPolicy, forSelf: true },
      });
      return;
    }
    navigate("/claim/new/relative", {
      state: { policy: mappedPolicy },
    });
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setDrawerOpen(false);
  };

  if (isLoading) {
    return <Loader fullScreen={true} text="Loading purchased policies..." />;
  }

  if (filteredPolicies.length === 0 && policies.length > 0) {
    return (
      <div className="mx-auto min-h-screen bg-white max-w-8xl">
        <div className="pt-24 lg:pt-28" />
        <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-between items-start mb-8">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
                My Policies
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage and view all your insurance policies
              </p>
            </div>
          </div>
          
          <div className="py-12 text-center">
            <p className="mb-4 text-gray-600">No policies match your current filters.</p>
            <button
              onClick={() => setFilters({ 
                status: { active: true, inactive: false }, 
                policyType: "all", 
                routeType: "all", 
                duration: "all" 
              })}
              className="px-4 py-2 text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (policies.length === 0) {
    return (
      <div className="mx-auto min-h-screen bg-white max-w-8xl">
        <div className="pt-24 lg:pt-28" />
        <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-between items-start mb-8">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
                My Policies
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage and view all your insurance policies
              </p>
            </div>
          </div>
          
          <EmptyState
            {...EmptyStatePresets.noPolicies}
            onAction={() => navigate("/insurance/choose")}
            size="lg"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen bg-white max-w-8xl">
      <div className="pt-24 lg:pt-28" />
      <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <div className="flex flex-wrap gap-4 justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
              Policy
            </h1>
            <p className="mt-1 text-sm text-gray-600 sm:text-base">
              Manage and view your policy details and actions
            </p>
          </div>

          <div className="flex gap-3 items-center">
            <button
              type="button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-700 to-green-800 shadow-md hover:from-emerald-800 hover:to-green-900 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
              onClick={() => navigate("/insurance/road-trip")}
            >
              <span className="flex gap-2 items-center">
                <Car />
                <span>Insure Your Trip</span>
              </span>
            </button>
            {/* Mobile/Tablet dropdown */}
            <div className="sm:hidden">
              <PolicyActionsDropdown onApply={(f) => handleApplyFilters(f)} />
            </div>
            {/* Desktop drawer trigger styled like mobile button */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-md hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-400/50"
            >
              <span className="flex gap-2 items-center">
                <SlidersHorizontal />
                <span>Filter</span>
              </span>
            </button>
          </div>
        </div>
        {/* Grid */}
        <div className="grid grid-cols-2 gap-2.5 mt-6 sm:gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3">
          {filteredPolicies.map((policy, index) => (
            <PolicyCard
              key={policy.insurance_policy_slug || index}
              policy={policy}
              onView={() => setViewTarget(policy)}
              onCancel={() => handleCancel(policy)}
              onClaim={() => handleClaim(policy)}
            />
          ))}
        </div>

        <ConfirmCancelModal
          open={!!cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleConfirmCancel}
          policyNumber={cancelTarget?.insurance_policy_slug?.slice(-8)}
          providerName={cancelTarget?.insurance_policy?.policy_name || 
                       cancelTarget?.purchased_policy_data?.product_name || 
                       "Policy"}
        />

        <CancelSuccessModal
          open={showSuccess}
          onClose={() => {
            setShowSuccess(false);
            setLastCancelled(null);
          }}
          policyNumber={lastCancelled?.insurance_policy_slug?.slice(-8)}
          providerName={lastCancelled?.insurance_policy?.policy_name || 
                       lastCancelled?.purchased_policy_data?.product_name || 
                       "Policy"}
        />

        <PolicyViewModal
          open={!!viewTarget}
          onClose={() => setViewTarget(null)}
          policy={viewTarget}
        />

        <PolicyholderCheckModal
          open={!!claimTarget}
          onClose={() => setClaimTarget(null)}
          onConfirm={handlePolicyholderChoice}
          policyName={
            claimTarget?.insurance_policy?.policy_name ||
            claimTarget?.purchased_policy_data?.product_name ||
            "this policy"
          }
        />

        <PolicyFilterDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onApply={handleApplyFilters}
          initial={filters}
        />
      </div>
    </div>
  );
};

export default Policy;
